import { createStackNavigator } from '@react-navigation/stack';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import ReservationsScreenType from 'src/domain/models/ReservationsScreenType';
import AccountButton from 'src/presentation/components/molecules/AccountButton';
import { useHeaderStyle } from 'src/presentation/hooks/useHeaderStyle';
import { useActiveLanguage } from 'src/presentation/hooks/useActiveLanguage';
import { headerBackButton, nestedStackScreenOptions } from 'src/presentation/navigation/defaultScreenOptions';
import type { AccountNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import AccountScreen from 'src/presentation/screens/AccountScreen';
import EditAddressScreen from 'src/presentation/screens/EditAddressScreen';
import EditProfileScreen from 'src/presentation/screens/EditProfileScreen';
import LanguagePreferencesScreen from 'src/presentation/screens/LanguagePreferencesScreen';
import MailPreferencesScreen from 'src/presentation/screens/MailPreferencesScreen';
import NotificationPreferencesScreen from 'src/presentation/screens/NotificationPreferencesScreen';
import PreferencesScreen from 'src/presentation/screens/PreferencesScreen';
import ProfileScreen from 'src/presentation/screens/ProfileScreen';
import ReservationDetailScreen from 'src/presentation/screens/ReservationDetailScreen';
import ReservationsScreen from 'src/presentation/screens/ReservationsScreen';
import TutorialScreen from 'src/presentation/screens/TutorialScreen';

const Stack = createStackNavigator<AccountNavigation>();

export default function AccountStackNavigator() {
	useActiveLanguage();

	const headerStyle = useHeaderStyle();

	const detailScreenOptions =
		({ title }: { title: string }) =>
		({ navigation: screenNavigation }: { navigation: { goBack: () => void } }) => ({
			title,
			headerStyle,
			headerLeft: () => headerBackButton({ navigation: screenNavigation }),
		});

	return (
		<Stack.Navigator screenOptions={nestedStackScreenOptions}>
			<Stack.Screen
				component={AccountScreen}
				name={Constants.screenAccountDetail}
				options={{
					title: Translations.headersAccount,
					headerStyle,
					headerRight: () => <AccountButton style={{ marginHorizontal: Dimens.padding18 }} />,
				}}
			/>
			<Stack.Screen
				component={ReservationsScreen}
				initialParams={{ screenType: ReservationsScreenType.upcoming }}
				name={Constants.screenUpcomingReservations}
				options={detailScreenOptions({ title: Translations.accountUpcomingReservations })}
			/>
			<Stack.Screen
				component={ReservationsScreen}
				initialParams={{ screenType: ReservationsScreenType.historical }}
				name={Constants.screenPastReservations}
				options={detailScreenOptions({ title: Translations.accountPastReservations })}
			/>
			<Stack.Screen
				component={ReservationDetailScreen}
				name={Constants.screenReservationDetail}
				options={detailScreenOptions({ title: Translations.reservationYourReservation })}
			/>
			<Stack.Screen
				component={PreferencesScreen}
				name={Constants.screenPreferences}
				options={detailScreenOptions({ title: Translations.accountMyPreferences })}
			/>
			<Stack.Screen
				component={NotificationPreferencesScreen}
				name={Constants.screenNotificationPreferences}
				options={detailScreenOptions({ title: Translations.accountPreferencesNotifications })}
			/>
			<Stack.Screen
				component={MailPreferencesScreen}
				name={Constants.screenMailPreferences}
				options={detailScreenOptions({ title: Translations.accountPreferencesEmails })}
			/>
			<Stack.Screen
				component={LanguagePreferencesScreen}
				name={Constants.screenLanguagePreferences}
				options={detailScreenOptions({ title: Translations.accountPreferencesPreferredLanguage })}
			/>
			<Stack.Screen
				component={ProfileScreen}
				name={Constants.screenProfile}
				options={detailScreenOptions({ title: Translations.accountMyDetails })}
			/>
			<Stack.Screen
				component={EditProfileScreen}
				name={Constants.screenEditProfile}
				options={detailScreenOptions({ title: Translations.accountChangePersonalDetails })}
			/>
			<Stack.Screen
				component={EditAddressScreen}
				name={Constants.screenEditAddress}
				options={detailScreenOptions({ title: Translations.accountChangeAdressDetails })}
			/>
			<Stack.Screen
				component={TutorialScreen}
				name={Constants.screenTutorial}
				options={detailScreenOptions({ title: Translations.videoTutorials })}
			/>
		</Stack.Navigator>
	);
}
